import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TicketHolder } from '@products/models/holder.model';
import { HolderService } from '@products/services/holder.service';
import { HOLDER_DEBOUNCE_TIME } from '@shared/tokens';
import { QuestionnaireDataSection } from '@store/customization/customization.interfaces';
import { CustomizationService } from '@store/customization/customization.service';
import { HelperService } from '@store/helpers/helper.service';
import { BookingActions } from '@store/products/booking/booking.actions';
import { HolderActions } from '@store/products/holder/holder.actions';
import { isEmpty, isNil } from 'lodash';
import { catchError, debounceTime, EMPTY, map, of, switchMap } from 'rxjs';

export const setHolderQuestionnaireInputs$ = createEffect(
  (
    actions$ = inject(Actions),
    holderDebounceTime = inject(HOLDER_DEBOUNCE_TIME, { optional: true }),
    holderService = inject(HolderService),
    customizationService = inject(CustomizationService)
  ) =>
    actions$.pipe(
      ofType(HolderActions.getQuestionnaireInputs),
      debounceTime(holderDebounceTime),
      switchMap(({ eventId, ticketPersonIds }) => {
        if (isNil(eventId)) {
          return EMPTY;
        }

        return holderService.getTariffHolderQuestionnaire$(eventId, ticketPersonIds).pipe(
          map((tariffHolderQuestionnaire: QuestionnaireDataSection[]) =>
            HolderActions.setQuestionnaireInputs(
              customizationService.transformQuestionnaireIntoInput(tariffHolderQuestionnaire)
            )
          ),
          catchError(() => of(HolderActions.setQuestionnaireInputs([])))
        );
      })
    ),
  { functional: true }
);

export const getClaimingTicketHolder$ = createEffect(
  (
    actions$ = inject(Actions),
    holderDebounceTime = inject(HOLDER_DEBOUNCE_TIME, { optional: true }),
    holderService = inject(HolderService),
    store = inject(Store)
  ) =>
    actions$.pipe(
      ofType(HolderActions.loadTicketHolder),
      debounceTime(holderDebounceTime),
      switchMap(({ hash }) => {
        if (isEmpty(hash)) {
          return EMPTY;
        }

        return holderService.getTicketByHolder(hash).pipe(
          map((ticketHolder: TicketHolder) => {
            // if we load the user
            if (Object.hasOwn(ticketHolder, 'id')) {
              store.dispatch(BookingActions.setIsClaimedTicketHashValid(true));
              return HolderActions.setTicketHolder(ticketHolder);
            } else {
              // else if the has is not valid, we need to give some feedback
              store.dispatch(BookingActions.setIsClaimedTicketHashValid(false));
              return HolderActions.setTicketHolder(null);
            }
          }),
          catchError(() => {
            store.dispatch(BookingActions.setIsClaimedTicketHashValid(false));
            return of(HolderActions.setTicketHolder(null));
          })
        );
      })
    ),
  { functional: true }
);

export const postTariffHolderQuestionnaire$ = createEffect(
  (
    actions$ = inject(Actions),
    holderDebounceTime = inject(HOLDER_DEBOUNCE_TIME, { optional: true }),
    holderService = inject(HolderService)
  ) =>
    actions$.pipe(
      ofType(HolderActions.postTicketHolderForm),
      debounceTime(holderDebounceTime),
      switchMap(({ ticketHolderForm }) => {
        if (ticketHolderForm === null) {
          return EMPTY;
        }

        return holderService.postTicketHolderForm(ticketHolderForm).pipe(
          map(() => HolderActions.setPostTicketHolderFormResult(true)),
          catchError(() => of(HolderActions.setPostTicketHolderFormResult(false)))
        );
      })
    ),
  { functional: true }
);

export const downloadTicket$ = createEffect(
  (
    actions$ = inject(Actions),
    holderDebounceTime = inject(HOLDER_DEBOUNCE_TIME, { optional: true }),
    holderService = inject(HolderService),
    helperService = inject(HelperService)
  ) =>
    actions$.pipe(
      ofType(HolderActions.downloadTicket),
      debounceTime(holderDebounceTime),
      switchMap(({ hash }) => {
        if (isEmpty(hash)) {
          return EMPTY;
        }

        return holderService.downloadTicket(hash).pipe(
          map((blob: any) => {
            helperService.saveToFileSystem(blob, 'ticket.pdf');

            // if we load the user
            return HolderActions.setTicketHolder(null);
          }),
          catchError(() => of(HolderActions.setTicketHolder(null)))
        );
      })
    ),
  { functional: true }
);

export const downloadMobileTicket$ = createEffect(
  (
    actions$ = inject(Actions),
    holderDebounceTime = inject(HOLDER_DEBOUNCE_TIME, { optional: true }),
    holderService = inject(HolderService),
    helperService = inject(HelperService)
  ) =>
    actions$.pipe(
      ofType(HolderActions.downloadMobileTicket),
      debounceTime(holderDebounceTime),
      switchMap(({ hash }) => {
        if (isEmpty(hash)) {
          return EMPTY;
        }

        return holderService.downloadMobileTicket(hash).pipe(
          map((blob: any) => {
            helperService.saveToFileSystem(blob, 'MobileTicket.pdf');

            // if we load the user
            return HolderActions.setTicketHolder(null);
          }),
          catchError(() => of(HolderActions.setTicketHolder(null)))
        );
      })
    ),
  { functional: true }
);

export const downloadPassBook$ = createEffect(
  (
    actions$ = inject(Actions),
    holderDebounceTime = inject(HOLDER_DEBOUNCE_TIME, { optional: true }),
    holderService = inject(HolderService),
    helperService = inject(HelperService)
  ) =>
    actions$.pipe(
      ofType(HolderActions.downloadPassBook),
      debounceTime(holderDebounceTime),
      switchMap(({ hash }) => {
        if (isEmpty(hash)) {
          return EMPTY;
        }

        return holderService.downloadPassBook(hash).pipe(
          map((blob: any) => {
            helperService.saveToFileSystem(blob, 'Passbook.pkpass');

            // if we load the user
            return HolderActions.setTicketHolder(null);
          }),
          catchError(() => of(HolderActions.setTicketHolder(null)))
        );
      })
    ),
  { functional: true }
);
